import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { MailCheck, RotateCw, ArrowLeft, AlertTriangle } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";

export default function PublicVerifyOTP() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useCustomerAuth();
  // Read email from (in order): router state → sessionStorage → query string
  const [email] = useState(() => {
    const fromState = loc.state?.email;
    if (fromState) {
      try { sessionStorage.setItem("aeoibcms_pending_verify_email", fromState); } catch { /* ignore */ }
      return fromState;
    }
    try {
      const fromStorage = sessionStorage.getItem("aeoibcms_pending_verify_email");
      if (fromStorage) return fromStorage;
    } catch { /* ignore */ }
    const params = new URLSearchParams(loc.search);
    return params.get("email") || "";
  });
  const nextPath = loc.state?.next || "/site/account";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const [deliveryWarning, setDeliveryWarning] = useState(loc.state?.emailDeliveryFailed || false);
  const [debugOtp, setDebugOtp] = useState(loc.state?.debugOtp || null);

  // Auto-fill from ?debug_otp= for testing or from router state (preview only)
  useEffect(() => {
    if (debugOtp && debugOtp.length === 6) {
      setDigits(debugOtp.split(""));
    }
  }, [debugOtp]);

  useEffect(() => {
    if (!email) nav("/site/register", { replace: true });
  }, [email, nav]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const data = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (data.length === 6) {
      e.preventDefault();
      setDigits(data.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    const otp = digits.join("");
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/customers/verify-otp", { tenant_slug: "abrah", email, otp });
      await refresh();
      // clear the pending email so the /site/register redirect can fire on subsequent visits
      try { sessionStorage.removeItem("aeoibcms_pending_verify_email"); } catch { /* ignore */ }
      toast.success("Email verified — welcome!");
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      const { data } = await api.post("/public/customers/resend-otp", { tenant_slug: "abrah", email });
      if (data?.email_delivery_failed) {
        setDeliveryWarning(true);
        if (data.debug_otp) setDebugOtp(data.debug_otp);
        toast.error(data.message || "Email delivery is currently unavailable.");
      } else {
        setDeliveryWarning(false);
        toast.success("A new code was sent to your inbox");
      }
      setResendIn(30);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code");
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 mb-4">
          <MailCheck size={26} className="text-emerald-800" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Check your email
        </h1>
        <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
          We sent a 6-digit verification code to{" "}
          <span className="font-semibold text-gray-900">{email}</span>.
          Enter it below to activate your account.
        </p>
      </div>
      {deliveryWarning && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2 items-start"
          data-testid="otp-delivery-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Email delivery is temporarily unavailable.</div>
            {debugOtp
              ? <>Testing code (preview only): <b className="font-mono">{debugOtp}</b></>
              : <>Please try Resend in 30 seconds, or contact support at{" "}
                  <a href="mailto:support@abrahtech.com" className="underline">support@abrahtech.com</a>.
                </>
            }
          </div>
        </div>
      )}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={submit} className="space-y-5" data-testid="otp-form">
            <div className="flex gap-2 justify-between" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <Input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => handleKey(i, e)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="h-14 text-center text-2xl font-mono font-semibold"
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>
            <Button
              type="submit"
              disabled={busy || digits.join("").length !== 6}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="otp-submit">
              {busy ? "Verifying…" : "Verify email"}
            </Button>
          </form>
          <div className="mt-5 pt-5 border-t border-gray-100 text-sm text-center text-gray-600">
            Didn&apos;t get the code?{" "}
            <button
              type="button"
              onClick={resend}
              disabled={resendIn > 0}
              data-testid="otp-resend"
              className={`inline-flex items-center gap-1 font-semibold ${
                resendIn > 0 ? "text-gray-400" : "text-emerald-800 hover:underline"
              }`}>
              <RotateCw size={12} className={resendIn > 0 ? "" : "animate-none"} />
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
            </button>
          </div>
          <div className="mt-4 text-center">
            <Link to="/site/register" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <ArrowLeft size={11} /> Use a different email
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
